/**
 * تگ‌های دنبال‌شده — قرینه‌ی apps/mobile/app/followed-tags.tsx.
 *
 * دنبال کردن یعنی «رخدادِ تازه با این تگ توی شهرم اومد، خبرم کن». خودِ خبر
 * دادن کارِ دیتابیسه (مایگریشن 0094)؛ این صفحه فقط فهرست رو با همون
 * TagPickerِ فرم‌ها ویرایش می‌کنه. کانالِ خبر یه تنظیمِ کلیه توی تنظیماتِ
 * اعلان، نه به‌ازای هر تگ.
 *
 * اجازه‌ی اعلانِ مرورگر همین‌جا هم پرسیده می‌شه: اولین دنبال کردن با
 * کانالِ پوش، اگه مرورگر هنوز نپرسیده باشه، همون لحظه می‌پرسه — کلیکِ
 * کاربره و مرورگر پنجره‌ی اجازه رو بدونِ غر نشون می‌ده.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tagFollowRepo, tagRepo } from '@/core/data/miscRepo';
import { DEFAULT_PREFS, notifPrefsRepo } from '@/core/data/notifPrefsRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Card, Note } from '@/ui/kit';
import { TagPicker } from '@/ui/TagPicker';
import { enableWebPush, pushConfigured, pushPermission } from '@/features/notifications/webPush';

export function FollowedTagsPage() {
  useLocale();
  const { profile } = useSession();
  const qc = useQueryClient();
  // انتخابِ محلی، تا منوی باز با هر کلیک منتظرِ رفت‌وبرگشتِ سرور نمونه.
  const [sel, setSel] = React.useState<number[] | null>(null);
  const [perm, setPerm] = React.useState(() => pushPermission());

  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });
  const follows = useQuery({
    queryKey: ['tagFollows'],
    queryFn: tagFollowRepo.list,
    enabled: !!profile,
  });
  const prefs = useQuery({
    queryKey: ['notifPrefs'],
    queryFn: notifPrefsRepo.mine,
    enabled: !!profile,
  });

  const value = sel ?? follows.data ?? [];
  const mode = (prefs.data ?? DEFAULT_PREFS).tag_follow;

  const change = useMutation({
    mutationFn: async ({ added, removed }: { added: number[]; removed: number[] }) => {
      for (const id of added) await tagFollowRepo.set(id, true, profile!.id);
      for (const id of removed) await tagFollowRepo.set(id, false, profile!.id);
      if (added.length && mode === 'push' && pushConfigured() && pushPermission() === 'default') {
        setPerm(await enableWebPush(profile!.id));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tagFollows'] }),
    // خطا یعنی انتخابِ محلی با سرور نمی‌خونه — از نو از سرور بخون.
    onError: (e) => { setSel(null); reportError('web.tagFollows.set', e); },
  });

  return (
    <div className="feed">
      <h1 className="feed-title">{t('followTags')}</h1>
      <p className="dim">{t('followTagsSub')}</p>

      {tags.isLoading || follows.isLoading ? <p className="dim">…</p> : (
        <Card>
          <TagPicker value={value} tags={tags.data ?? []}
            onChange={(next) => {
              const added = next.filter((id) => !value.includes(id));
              const removed = value.filter((id) => !next.includes(id));
              setSel(next);
              change.mutate({ added, removed });
            }} />
        </Card>
      )}

      {/* کانالش پوشه ولی مرورگر اجازه نداده (یا اصلاً نمی‌تونه) — تنها
          حالتی که این صفحه چیزی وعده می‌ده که نمی‌رسه. کانال خودش این‌جا
          تکرار نمی‌شه؛ جاش توی منوی تنظیمات → اعلان‌هاست (بازخورد ۲۰۲۶-۰۸-۲۱). */}
      {mode === 'push' && perm !== 'granted' && pushConfigured()
        ? <Note tone="warn">{t('followTagsNoPush')}</Note> : null}
    </div>
  );
}
